'use client';

import BaseSpinner from '@module/components/Spinner/BaseSpinner';
import { cn } from '@module/lib/utils';
import throttle from '@module/utils/throttle';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import React, { useEffect, useMemo, useRef } from 'react';

type BaseButtonVariant = 'ghost' | 'default' | 'primary' | 'secondary' | 'gray' | 'disabled';
export type BaseButtonSize = 'sm' | 'none' | 'md' | 'icon';

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BaseButtonVariant;
  type?: 'button' | 'submit' | 'reset';
  size?: BaseButtonSize;
  isThrottle?: boolean;
  throttleDelay?: number;
  ref?: React.RefObject<HTMLButtonElement | null>;
  tooltip?: {
    content: string | React.ReactNode;
    side?: 'bottom' | 'top' | 'right' | 'left';
    className?: string;
    isOpen?: boolean;
    escapeOverflow?: boolean;
  };
  onClick?: () => void;
  side?: 'bottom' | 'top' | 'right' | 'left';
  contents?: string | React.ReactNode;
  isPending?: boolean;
}

type TooltipSide = NonNullable<NonNullable<Props['tooltip']>['side']>;

const TOOLTIP_CLASS_NAME =
  'pointer-events-none whitespace-nowrap rounded-[10px] bg-colorBg_tooltip px-4 py-[9px] text-sm text-tooltipColr shadow-md transition-opacity duration-150';

const getSizeClassName = (size?: BaseButtonSize) => {
  switch (size) {
    case 'none':
      return '';
    case 'icon':
      return 'whitespace-nowrap px-2.5 rounded-lg';
    case 'sm':
      return 'whitespace-nowrap px-[16px] h-[40px] rounded-[8px] text-[15px]';
    case 'md':
      return 'whitespace-nowrap px-[20px] h-[48px] rounded-[8px] text-[17px]';
    default:
      return 'whitespace-nowrap px-[20px] h-[56px] rounded-[14px]';
  }
};

const getVariantClassName = (variant: BaseButtonVariant) => {
  switch (variant) {
    case 'primary':
      return 'bg-primaryBg text-colorFg12';
    case 'gray':
      return 'bg-colorBg12 text-colorFg12';
    case 'secondary':
      return 'bg-secondary text-primaryFg';
    case 'ghost':
      return 'text-baseFg';
    case 'disabled':
      return 'bg-colorBg06 text-center text-colorFg16';
    default:
      return 'bg-colorBg15 border border-borderBg18';
  }
};

const getTooltipPositionClassName = (side: TooltipSide = 'bottom') => {
  switch (side) {
    case 'top':
      return 'bottom-[calc(100%+6px)] left-1/2 -translate-x-1/2';
    case 'left':
      return 'top-1/2 right-[calc(100%+6px)] -translate-y-1/2';
    case 'right':
      return 'top-1/2 left-[calc(100%+6px)] -translate-y-1/2';
    case 'bottom':
    default:
      return 'top-[calc(100%+6px)] left-1/2 -translate-x-1/2';
  }
};

const getTooltipVisibilityClassName = (isOpen?: boolean) => {
  if (isOpen === true) return 'visible opacity-100';
  if (isOpen === false) return 'invisible opacity-0';
  return 'invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100';
};

const BaseButton = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      className,
      children,
      contents,
      side = 'bottom',
      onClick,
      size,
      variant = 'default',
      type = 'button',
      throttleDelay = 500,
      isThrottle = true,
      tooltip,
      isPending = false,
      ...rest
    },
    ref,
  ) => {
    const onClickRef = useRef(onClick);

    useEffect(() => {
      onClickRef.current = onClick;
    }, [onClick]);

    const throttleRef = useRef(
      throttle(() => {
        if (onClickRef.current) {
          onClickRef.current();
        }
      }, throttleDelay),
    );

    const variantClassName = getVariantClassName(variant);
    const sizeClassName = getSizeClassName(size);

    const childrenOrPending = useMemo(() => {
      if (isPending) return <BaseSpinner size={24} />;
      return children;
    }, [isPending, children]);

    const buttonClassName = cn(
      'group relative flex items-center justify-center transition-colors duration-700 hover:underline hover:underline-offset-2 focus:outline-none active:outline-none active:ring-0',
      variantClassName,
      sizeClassName,
      {
        'bg-colorBg06 text-center text-colorFg16': !!rest.disabled && variant !== 'default',
        'text-colorFg04': !rest.disabled && variant === 'default',
        'bg-colorBg06 text-colorFg04': !!rest.disabled && variant === 'default',
      },
      className,
    );

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (isThrottle) {
        throttleRef.current();
      } else if (onClick && !isPending) {
        onClick();
      }
    };

    const tooltipSide = tooltip?.side ?? side;

    const button = (
      <button {...rest} ref={ref} className={buttonClassName} type={type} onClick={handleClick}>
        {childrenOrPending}
        {tooltip && !tooltip.escapeOverflow && (
          <span
            aria-hidden='true'
            className={cn(
              'absolute z-[30]',
              TOOLTIP_CLASS_NAME,
              getTooltipPositionClassName(tooltipSide),
              getTooltipVisibilityClassName(tooltip.isOpen),
              tooltip.className,
            )}
          >
            {tooltip.content}
          </span>
        )}
      </button>
    );

    if (!tooltip?.escapeOverflow) return button;

    return (
      <TooltipPrimitive.Root delayDuration={0} open={tooltip.isOpen}>
        <TooltipPrimitive.Trigger asChild>{button}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={tooltipSide}
            align='center'
            sideOffset={6}
            className={cn('z-[60]', TOOLTIP_CLASS_NAME, tooltip.className)}
            avoidCollisions={false}
            style={{ animation: 'none' }}
          >
            {tooltip.content}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    );
  },
);

BaseButton.displayName = 'BaseButton';

export default BaseButton;
